/**
 * Authentication Context
 */

import React, { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { authService, User } from '../services/authApi';
import { clearAllChatDrafts, rememberChatDraftOwner } from '../utils/chatDraft';
import { resetFolderTreeStore } from '../services/folderTreeStore';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  /**
   * Create an account and sign in.
   *
   * Resolves to `{ pendingApproval: true }` when the deployment holds new
   * accounts for an operator (DOP-295). That is a SUCCESS -- the account
   * exists -- so it is a return value and not a thrown error; auto-login is
   * skipped, because an inactive account is refused with
   * LOGIN_BAD_CREDENTIALS and the person would be told their brand-new
   * password is wrong.
   */
  register: (
    email: string,
    password: string,
    fullName?: string,
  ) => Promise<{ pendingApproval: boolean }>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  completeOAuthLogin: (token: string, user: User) => void;
  /** Re-read the signed-in user, e.g. after /settings renames the account. */
  refreshUser: () => Promise<void>;
  /** Install a replacement JWT, e.g. after a password change revokes the old one. */
  replaceToken: (token: string) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * The auth context when one is mounted, or undefined — for per-user view
 * preferences that must still render (unscoped) outside the provider.
 */
export const useOptionalAuth = () => useContext(AuthContext);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUserState] = useState<User | null>(null);

  // The shared Deal Room tree belongs to one principal. Whenever the signed-in
  // user changes (sign-out, an expired session, a different login), drop it
  // BEFORE the new user renders, so no screen ever shows the previous one's
  // deals, even for a moment. The tab's first sign-in has nothing to drop.
  const folderTreeOwnerRef = useRef<string | null>(null);
  const setUser = useCallback((next: User | null) => {
    const id = next ? String(next.id) : null;
    if (folderTreeOwnerRef.current !== null && folderTreeOwnerRef.current !== id) {
      resetFolderTreeStore();
    }
    folderTreeOwnerRef.current = id;
    setUserState(next);
  }, []);
  const [loading, setLoading] = useState(true);

  // Once per provider lifetime: StrictMode's dev remount re-runs the effect,
  // and without the guard every app boot pays a duplicate `users/me` fetch.
  // The ref survives the simulated remount, so the second pass no-ops.
  const initAuthStartedRef = useRef(false);

  useEffect(() => {
    if (initAuthStartedRef.current) return;
    initAuthStartedRef.current = true;
    // Check if user is already logged in
    const initAuth = async () => {
      const token = authService.getToken();
      const hasToken = authService.isAuthenticated();
      console.log('[AuthContext] Init auth - has token:', hasToken);
      console.log('[AuthContext] Init auth - token exists:', !!token);
      console.log('[AuthContext] Init auth - token preview:', token ? token.substring(0, 20) + '...' : 'null');

      if (hasToken) {
        try {
          const currentUser = await authService.getCurrentUser();
          console.log('[AuthContext] Init auth - user fetched:', currentUser.email);
          // Composer drafts live in sessionStorage, which outlives a logout;
          // binding them to the signed-in user keeps them from leaking to
          // whoever signs in next on this tab (DOP-267).
          rememberChatDraftOwner(currentUser.id);
          setUser(currentUser);
        } catch (error) {
          console.error('[AuthContext] Failed to fetch user, clearing auth:', error);
          authService.removeToken();
          setUser(null);
        }
      } else {
        console.log('[AuthContext] Init auth - no token, clearing user');
        setUser(null);
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      console.log('[AuthContext] Starting login process...');
      const loginResponse = await authService.login(email, password);
      console.log('[AuthContext] Login response received:', loginResponse);
      const currentUser = await authService.getCurrentUser();
      console.log('[AuthContext] Current user fetched:', currentUser);
      // A different principal than the tab last held drops every stored draft.
      rememberChatDraftOwner(currentUser.id);
      setUser(currentUser);
    } catch (error) {
      console.error('[AuthContext] Login error:', error);
      throw error;
    }
  };

  const register = async (email: string, password: string, fullName?: string) => {
    try {
      console.log('[AuthContext] Starting registration process...');
      const created = await authService.register({
        email,
        password,
        full_name: fullName,
      });

      // The deployment is holding the account for an operator. Logging in
      // would fail as LOGIN_BAD_CREDENTIALS, which reads as "your password is
      // wrong" for someone who just chose it.
      if (created && created.is_active === false) {
        console.log('[AuthContext] Account created but pending approval');
        return { pendingApproval: true };
      }
      console.log('[AuthContext] Registration successful, attempting auto-login...');
      // Auto-login after registration
      await login(email, password);
      console.log('[AuthContext] Auto-login successful');
      return { pendingApproval: false };
    } catch (error) {
      console.error('[AuthContext] Registration/login error:', error);
      throw error;
    }
  };

  const logout = async () => {
    console.log('[AuthContext] Logging out...');
    // Clear user state immediately
    setUser(null);
    // Nothing the user typed may survive into the next session on this tab.
    clearAllChatDrafts();
    rememberChatDraftOwner(null);

    try {
      // Try to call logout API (best effort)
      await authService.logout();
    } catch (error) {
      console.error('[AuthContext] Logout API error (continuing anyway):', error);
    }

    // Ensure token is removed
    authService.removeToken();
    console.log('[AuthContext] Token removed, redirecting to login');

    // Force redirect to login page
    window.location.href = '/login';
  };

  const refreshUser = useCallback(async () => {
    if (!authService.isAuthenticated()) return;
    setUser(await authService.getCurrentUser());
  }, []);

  const replaceToken = useCallback((token: string) => {
    authService.setToken(token);
  }, []);

  const completeOAuthLogin = (token: string, currentUser: User) => {
    console.log('[AuthContext] Completing OAuth login for user:', currentUser.email);
    console.log('[AuthContext] Token preview:', token.substring(0, 20) + '...');
    authService.setToken(token);
    rememberChatDraftOwner(currentUser.id);
    setUser(currentUser);
    setLoading(false);
    console.log('[AuthContext] OAuth login completed, user state updated');
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!user,
    completeOAuthLogin,
    refreshUser,
    replaceToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
